import Backbone from 'backbone';
import { isString, isUndefined } from 'lodash';
import { tagInterface } from '@ardoq/tag-interface';
import { AttributeComparators } from '../collections/attributeComparators';
import {
  JsonFilterPrefixes,
  isDefaultComponentAttribute,
  isDefaultReferenceAttribute,
} from './filterEnums';
import {
  defaultFilterToArray,
  labelFormattingFilterToArray,
  parseUriEncoded,
  systemFilterToArray,
} from 'models/utils/filterURLEncodeDecode';
import { isUndefinedOrNull } from 'utils/collectionUtil';
import { logError } from '@ardoq/logging';
import { SpecialFilterKeys } from '@ardoq/query-builder';
import {
  CommonDefaultFilterKeys,
  ComponentDefaultFilterKeys,
  DynamicFilterValues,
  isResult,
  isSpecialAttributeType,
  parseFilterValue,
  isHasReferenceToFilter,
  isHasReferenceFromFilter,
  shouldModelWithoutFieldBeIncluded,
  isEntityTypeIncluded,
  legacyAffectsTypeNames,
  toNumericFieldValue,
  type AttributeComparator,
  isListFieldIncluded,
} from '@ardoq/filter-helpers';
import { ReferenceAdjacency } from '@ardoq/data-model';
import { LinkedComponent } from '@ardoq/api-types';
import type { Filter as FilterBackboneModel } from 'aqTypes';
import {
  APIEntityType,
  type APIComponentAttributes,
  type APIFieldAttributes,
  type APIReferenceAttributes,
  type ArdoqId,
  BooleanOperator,
  FilterInfoOperator,
  FilterTypes,
  isListFieldFormatting,
  isSystemFilter,
  type FilterAttributes,
  type FormattingOperator,
} from '@ardoq/api-types';
import { contextInterface } from '../modelInterface/contextInterface';
import { componentInterface } from '@ardoq/component-interface';
import { dateRangeOperations } from '@ardoq/date-range';
import { fieldInterface } from '@ardoq/field-interface';
import type { globalFilters as Filters } from 'collections/filters';
import { isRuleVersionApplicable } from 'collections/isRuleVersionApplicable';
import { fieldOperations, filterOperations } from '@ardoq/core';
import {
  getColorRangeFormatting,
  getListFieldFormatting,
  getRelativeDateValue,
  isDateInRelativeDateRange,
} from '@ardoq/filter-helpers';
import { sortListFieldFormatting } from '@ardoq/perspectives';
import { areAttributesIncluded } from './utils/areAttributesIncluded';
import { coerceComparator } from './utils/coerceComparator';
import { backboneFilterToFilterAttributes } from '../collections/toPureDataFilterUtils';
import { Extend } from '@ardoq/common-helpers';

/**
 * The types of the `.attributes` property of the Filter Backbone model.
 * This is _almost_ the same as `FilterAttributes`, except that `rules` are
 * backbone models instead of filter attributes / plain objects.
 */
type BackboneFilterAttributes = Extend<
  FilterAttributes,
  {
    rules?: FilterBackboneModel[];
  }
>;

/**
 * call this function only when the filter value is "current component."
 * it will resolve the value based on which attribute is being matched,
 * or just return "current component" back, according to the idiosyncratic and often
 * inconsistent rules by which this feature was implemented.
 */
const resolveCurrentComponentAttributeFilterValue = (
  filterAttributeName: string
) => {
  if (filterAttributeName === ComponentDefaultFilterKeys.COMPONENT_KEY) {
    const selectedComponentId = contextInterface.getCurrentComponentId() ?? '';
    return componentInterface.get(selectedComponentId)?.[
      ComponentDefaultFilterKeys.COMPONENT_KEY
    ];
  }
  return DynamicFilterValues.COMPONENT_CURRENT;
};

function findFieldByName(
  fields: APIFieldAttributes[],
  filteredAttributeName: string
) {
  return fields.find(field => field.name === filteredAttributeName);
}

const hasLinkedComponent = (
  linkedComponents: LinkedComponent[] | undefined,
  componentId: ArdoqId
): boolean => {
  if (!linkedComponents) return false;
  return linkedComponents.some(linked => linked.componentId === componentId);
};

function handleSpecialAttributeType(
  referenceAdjacency: ReferenceAdjacency,
  filterAttributeName: SpecialFilterKeys,
  filterValue: string,
  componentId: string
) {
  const filterValueOrResult = parseFilterValue({
    filterValue,
    currentComponentId: contextInterface.getCurrentComponentId() ?? '',
  });

  if (isResult(filterValueOrResult)) {
    return filterValueOrResult;
  }

  if (isHasReferenceToFilter(filterAttributeName)) {
    return hasLinkedComponent(
      referenceAdjacency.targetMap.get(filterValueOrResult),
      componentId
    );
  }
  if (isHasReferenceFromFilter(filterAttributeName)) {
    return hasLinkedComponent(
      referenceAdjacency.sourceMap.get(filterValueOrResult),
      componentId
    );
  }
  return false;
}

/**
 * Check whether a model has an attribute.
 *
 * A model (reference or component) is considered to have an attribute if the
 * attribute is either a default attribute for that model type (`target` for
 * example), or if the type of the model has a custom field with that name
 * defined.
 *
 * @param obj The component or reference instance/object to check
 * @param filteredAttributeName The name of the attribute to check for
 */
const modelHasAttributeWithField = (
  entityType: APIEntityType,
  fields: APIFieldAttributes[],
  filteredAttributeName: string
) => {
  const defaultComponentAttribute =
    entityType === APIEntityType.COMPONENT &&
    isDefaultComponentAttribute(filteredAttributeName);
  const defaultReferenceAttribute =
    entityType === APIEntityType.REFERENCE &&
    isDefaultReferenceAttribute(filteredAttributeName);

  if (defaultComponentAttribute || defaultReferenceAttribute) {
    return { hasAttribute: true };
  }
  const field = findFieldByName(fields, filteredAttributeName);
  return field ? { hasAttribute: true, field } : { hasAttribute: false };
};

const Filter: typeof FilterBackboneModel = Backbone.Model.extend({
  defaults: {
    comparator: undefined,
    affectComponent: false,
    affectReference: false,
    isNegative: false,
    name: '', // attribute filters will lookup this key
    tag: undefined,
    type: undefined, // attribute|tag
    value: '', // the actual value being searched for
  },
  isComponentFormatting: function (this: FilterBackboneModel) {
    return filterOperations.isComponentFormatting(this.attributes);
  },
  isReferenceFormatting: function (this: FilterBackboneModel) {
    return filterOperations.isReferenceFormatting(this.attributes);
  },

  initialize: function (
    this: FilterBackboneModel,
    attributes: BackboneFilterAttributes | string
  ) {
    if (isString(attributes)) {
      const filterAttributes = parseUriEncoded(attributes);

      if (isRuleVersionApplicable((filterAttributes as any)?.version)) {
        // Re-sort ListFieldFormatting keys to match field-definition order
        // before calling set, so there's only a single set call and no risk
        // of the firstPass branch in the custom set method clobbering the
        // comparator. When a filter is loaded from the URL the value is
        // deserialized from a compact JSON array whose key order may differ
        // from the field's canonical selectOptions order, causing the legend
        // to display items in the wrong order (ARD-31036).
        const attrs = filterAttributes as FilterAttributes;
        if (
          attrs.name &&
          attrs.value !== null &&
          attrs.value !== undefined &&
          isListFieldFormatting(attrs.value)
        ) {
          const fieldAttributes = fieldInterface.getGlobalFieldByName(
            attrs.name
          );
          if (typeof fieldAttributes?.defaultValue === 'string') {
            const listFieldOptions = fieldAttributes.defaultValue.split(',');
            if (listFieldOptions.length) {
              attrs.value = sortListFieldFormatting(
                attrs.value,
                listFieldOptions
              );
            }
          }
        }

        this.set(/** @type {FilterAttributes} */ filterAttributes);
      }
    }

    return this;
  },
  destroy: function (this: FilterBackboneModel, options = {}) {
    if (this.attributes.rules) {
      (this.attributes.rules as FilterBackboneModel[]).forEach(filter =>
        filter.destroy()
      );
    }
    if (this.collection) {
      (this.collection as typeof Filters).removeFilter(this, {
        shouldTriggerChangeEvent: false,
      });
    }
    Backbone.Model.prototype.destroy.call(this, options);
  },
  getComparator: function (this: FilterBackboneModel) {
    return AttributeComparators.getById(this.get('comparator'));
  },
  getAvailableComparators: function (this: FilterBackboneModel) {
    return AttributeComparators.getAvailableComparators(this.get('name'));
  },
  isCompLabelFilter: function (this: FilterBackboneModel): boolean {
    return (
      this.attributes.type === FilterTypes.COMPONENT_LABEL &&
      this.get('affectComponent')
    );
  },
  isRefLabelFilter: function (this: FilterBackboneModel): boolean {
    return (
      this.attributes.type === FilterTypes.REFERENCE_LABEL &&
      this.get('affectReference')
    );
  },
  isWorkspaceFilter: function (this: FilterBackboneModel) {
    return (
      this.collection &&
      (this.collection as typeof Filters).workspaceFilter === this
    );
  },
  isAffected: function (this: FilterBackboneModel, entityType: APIEntityType) {
    return (
      (this.get('affectComponent') && entityType === APIEntityType.COMPONENT) ||
      (this.get('affectReference') && entityType === APIEntityType.REFERENCE)
    );
  },
  isIncluded: function (
    this: FilterBackboneModel,
    entityType: APIEntityType,
    attributes: APIComponentAttributes | APIReferenceAttributes,
    fields: APIFieldAttributes[],
    referenceAdjacency: ReferenceAdjacency,
    referenceTypeName: string | null
  ) {
    const {
      condition,
      rules,
      type,
    }: {
      condition: BooleanOperator;
      rules: FilterBackboneModel[];
      type: FilterTypes.ATTRIBUTE | FilterTypes.TAG | FilterTypes.DATE_RANGE;
    } = this.attributes;
    if (condition) {
      if (condition === BooleanOperator.AND)
        return rules.every(rule =>
          rule.isIncluded(
            entityType,
            attributes,
            fields,
            referenceAdjacency,
            referenceTypeName
          )
        );
      if (condition === BooleanOperator.OR)
        return rules.some(rule =>
          rule.isIncluded(
            entityType,
            attributes,
            fields,
            referenceAdjacency,
            referenceTypeName
          )
        );
    }
    const filterTypeHandlers = {
      [FilterTypes.ATTRIBUTE]: () =>
        this.isAttributeIncluded(
          entityType,
          attributes,
          fields,
          referenceAdjacency,
          referenceTypeName
        ),
      [FilterTypes.DATE_RANGE]: () =>
        this.isAttributeIncluded(
          entityType,
          attributes,
          fields,
          referenceAdjacency,
          referenceTypeName
        ),
      [FilterTypes.TAG]: () => this.isTagIncluded(entityType, attributes._id),
      [FilterTypes.SYSTEM_FILTER]: () => true,
    };
    return filterTypeHandlers[type]();
  },
  isTagIncluded: function (
    this: FilterBackboneModel,
    entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
    id: ArdoqId
  ) {
    if (!this.isAffected(entityType)) {
      return true;
    }
    const tagName: string = this.get('name');
    const isIncluded = tagInterface.entityHasTag(tagName, entityType, id);
    return this.get('isNegative') ? !isIncluded : isIncluded;
  },
  isAttributeIncluded: function (
    this: FilterBackboneModel,
    entityType: APIEntityType,
    attributes: APIComponentAttributes | APIReferenceAttributes,
    fields: APIFieldAttributes[],
    referenceAdjacency: ReferenceAdjacency,
    referenceTypeName: string | null
  ) {
    if (!this.isAffected(entityType)) {
      return true;
    }

    // TODO(typeName-migration): Remove affectsTypeName and legacyAffectsTypeNames; use affectsTypeNames directly.
    const affectsTypeName: string | undefined = this.get('affectsTypeName');
    const affectsTypeNames: string[] | undefined = this.get('affectsTypeNames');
    const typeNameFilter = legacyAffectsTypeNames(
      affectsTypeName,
      affectsTypeNames
    );
    const isFormattingFilter: boolean = !!this.get('color');
    const filterAttributeName: string = this.get('name');

    // Short-circuit: If this is a formatting filter scoped to specific type name(s),
    // and the entity being tested is not one of those types, return false.
    // Skip for the 'type' attribute itself to avoid double-filtering.
    if (
      isFormattingFilter &&
      typeNameFilter &&
      !isEntityTypeIncluded({
        filterAttributeName,
        typeNameFilter,
        entityTypeName:
          entityType === APIEntityType.COMPONENT
            ? (attributes as APIComponentAttributes).type
            : referenceTypeName,
      })
    ) {
      return false;
    }

    const isNegative: boolean = this.get('isNegative');
    const filterValue = this.get('value');
    const filterType = this.get('type');
    const filterComparator = this.get('comparator');
    const comparator = this.getComparator();
    if (filterAttributeName.includes(',')) {
      return areAttributesIncluded({
        filterAttributeNames: filterAttributeName.split(','),
        isNegative,
        filterValue,
        isFormattingFilter,
        filterType,
        filterComparator,
        comparator,
        attributes,
      });
    }

    if (isSpecialAttributeType(filterAttributeName)) {
      const isIncluded = handleSpecialAttributeType(
        referenceAdjacency,
        filterAttributeName,
        filterValue,
        attributes._id
      );

      return isNegative ? !isIncluded : isIncluded;
    }
    const hasAttributeAndField = modelHasAttributeWithField(
      entityType,
      fields,
      filterAttributeName
    );
    const { field } = hasAttributeAndField;
    let { hasAttribute } = hasAttributeAndField;
    let val = (attributes as Record<string, unknown>)[filterAttributeName];

    if (
      !val &&
      filterAttributeName === ComponentDefaultFilterKeys.INCOMING_REF_COUNT
    ) {
      hasAttribute = true;
      val = (referenceAdjacency.targetMap.get(attributes._id) ?? []).length;
    } else if (
      !val &&
      filterAttributeName === ComponentDefaultFilterKeys.OUTGOING_REF_COUNT
    ) {
      hasAttribute = true;
      val = (referenceAdjacency.sourceMap.get(attributes._id) ?? []).length;
    } else if (
      filterAttributeName === CommonDefaultFilterKeys.TYPE &&
      this.get('affectReference')
    ) {
      val = referenceTypeName;
    }

    if (hasAttribute) {
      if (
        field &&
        fieldOperations.isDateTimeOrDateOnlyField(field) &&
        dateRangeOperations.fieldNameIsPartOfDateRangeField(
          filterAttributeName
        ) &&
        comparator?.id !== FilterInfoOperator.EQUALS
      ) {
        const counterpartField = findFieldByName(
          fields,
          dateRangeOperations.getDateRangeDateTimeFieldCounterpartName(
            filterAttributeName
          )
        );
        val = dateRangeOperations.replaceEmptyDateRangePartFieldValue({
          fieldName: filterAttributeName,
          fieldType: field.type,
          currentFieldValue: fieldOperations.getFieldValue(field, attributes),
          dateRangeFieldCounterpartValue:
            counterpartField &&
            fieldOperations.isDateTimeOrDateOnlyField(counterpartField)
              ? fieldOperations.getFieldValue(counterpartField, attributes)
              : null,
        });
      }

      const listFieldFormatting = getListFieldFormatting(
        isFormattingFilter,
        filterValue,
        filterType
      );
      if (listFieldFormatting) {
        return isListFieldIncluded(
          val as string | undefined,
          listFieldFormatting
        );
      }

      const colorRangeFormatting = getColorRangeFormatting(
        isFormattingFilter,
        filterValue,
        filterType
      );
      if (colorRangeFormatting) {
        return toNumericFieldValue(val) !== undefined;
      }

      const relativeDateValue = getRelativeDateValue(
        isFormattingFilter,
        filterValue,
        filterType,
        filterComparator
      );
      if (relativeDateValue) {
        const isInRange = isDateInRelativeDateRange(val, relativeDateValue);
        return isNegative ? !isInRange : isInRange;
      }

      const resolvedFilterValue =
        filterValue === DynamicFilterValues.COMPONENT_CURRENT
          ? resolveCurrentComponentAttributeFilterValue(filterAttributeName)
          : filterValue;
      const isIncluded = comparator?.isIncluded(val, resolvedFilterValue);
      if (isNegative) return !isIncluded;
      return isIncluded;
    }
    return shouldModelWithoutFieldBeIncluded({
      isNegative,
      value: filterValue,
      comparator: filterComparator,
    });
  },
  isDynamic: function (this: FilterBackboneModel) {
    const { condition, rules, value } = this.attributes;
    if (condition === BooleanOperator.AND || condition === BooleanOperator.OR) {
      return (rules as FilterBackboneModel[]).some(rule => rule.isDynamic());
    }
    return Object.keys(DynamicFilterValues).some(
      key =>
        value === DynamicFilterValues[key as keyof typeof DynamicFilterValues]
    );
  },
  set: function (
    this: FilterBackboneModel,
    key: keyof BackboneFilterAttributes | BackboneFilterAttributes,
    val?: any,
    options?: Backbone.ModelSetOptions
  ) {
    let attrs: BackboneFilterAttributes;
    if (isUndefinedOrNull(key)) {
      return this;
    }
    let actualOptions = options;
    // Handle both `"key", value` and `{key: value}` -style arguments.
    if (typeof key === 'object') {
      attrs = key;
      actualOptions = val;
    } else {
      ((attrs = {}) as BackboneFilterAttributes)[key] = val;
    }
    actualOptions = actualOptions || {};

    const firstPass = isUndefined(this.collection);
    if (firstPass) {
      attrs.comparator =
        attrs.comparator ||
        ((
          AttributeComparators.getDefaultComparator(
            attrs.name || this.get('name')
          ) as AttributeComparator
        ).id as FilterInfoOperator);
      attrs.type = attrs.type || FilterTypes.ATTRIBUTE;
    }

    // Force valid comparator
    const isAttr =
      (!attrs.type && this.get('type') === 'attribute') ||
      attrs.type === 'attribute';
    const comparatorId: FormattingOperator =
      attrs.comparator || this.get('comparator');
    const isIsEmptyFilter =
      isAttr && comparatorId === 'equals' && attrs.value === null;
    if (!isIsEmptyFilter && isAttr && attrs.name) {
      const coercedComparator = coerceComparator(attrs.name, comparatorId);
      if (coercedComparator !== comparatorId) {
        attrs.comparator = coercedComparator as FilterInfoOperator;
      }
    }

    Backbone.Model.prototype.set.call(this, attrs, actualOptions);

    return this;
  },
  setRules(this: FilterBackboneModel, rules: FilterBackboneModel[]) {
    Backbone.Model.prototype.set.call(this, { rules }, {});
    return this;
  },

  encodeURI: function (this: FilterBackboneModel) {
    try {
      if (this.isCompLabelFilter() || this.isRefLabelFilter()) {
        return `${JsonFilterPrefixes.LABEL_FORMATTING}${window.encodeURI(
          JSON.stringify(labelFormattingFilterToArray(this.attributes))
        )}&`;
      } else if (this.isWorkspaceFilter()) {
        return `${JsonFilterPrefixes.WORKSPACE_FILTER}${window.encodeURI(
          JSON.stringify(
            defaultFilterToArray(backboneFilterToFilterAttributes(this))
          )
        )}&`;
      } else if (isSystemFilter(this.attributes)) {
        return `${JsonFilterPrefixes.SYSTEM_FILTER}${window.encodeURI(
          JSON.stringify(systemFilterToArray(this.attributes))
        )}&`;
      }
      return `${JsonFilterPrefixes.DEFAULT}${window.encodeURI(
        JSON.stringify(
          defaultFilterToArray(backboneFilterToFilterAttributes(this))
        )
      )}&`;
    } catch (error) {
      logError(error as Error, 'Filter.prototype.encodeURI failed');
      return '';
    }
  },
});

export { Filter };
